import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trash, Plus, Save, X } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";

const TIERS = [
  { v: "both", l: "Her İkisi" },
  { v: "standard", l: "Standart" },
  { v: "professional", l: "Profesyonel" },
];

export default function AdminFurnitureTypes() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [legs, setLegs] = useState([]);
  const [covers, setCovers] = useState([]);

  const load = useCallback(async () => {
    const [t, l, c] = await Promise.all([
      api.get("/furniture-types").then(r => r.data),
      api.get("/leg-types").then(r => r.data),
      api.get("/cover-types").then(r => r.data),
    ]);
    setItems(t); setLegs(l); setCovers(c);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({
    __new: true, name: "", slug: "", description: "", image_url: "", preview_url: "",
    tier: "both", icon: "sofa", order: items.length + 1, active: true,
    variations: [], allowed_leg_ids: [], allowed_cover_ids: [], extra_fields: [],
    dimension_mode: "wh", info_text: "",
    default_width_cm: 0, default_height_cm: 0, default_depth_cm: 60,
  });

  const save = async () => {
    const body = { ...editing };
    delete body.__new;
    delete body.id;
    delete body.created_at;
    delete body.updated_at;
    // Strip client-side keys from nested arrays
    body.variations = (body.variations || []).map((v) => {
      if (typeof v === "string") return { name: v, extra_price: 0, price_mode: "flat" };
      const { _ck, ...rest } = v;
      return {
        name: rest.name || "",
        extra_price: Number(rest.extra_price || 0),
        price_mode: rest.price_mode === "per_m2" ? "per_m2" : "flat",
      };
    });
    body.extra_fields = (body.extra_fields || []).map(({ _ck, options, ...rest }) => ({
      ...rest,
      options: (options || []).map((o) => {
        if (typeof o === "string") return { name: o, extra_price: 0, price_mode: "flat" };
        const { _ck: _ock, ...opt } = o;
        return {
          name: opt.name || "",
          extra_price: Number(opt.extra_price || 0),
          price_mode: opt.price_mode === "per_m2" ? "per_m2" : "flat",
        };
      }),
    }));
    try {
      if (editing.__new) { await api.post("/furniture-types", body); toast.success("Eklendi"); }
      else { await api.put(`/furniture-types/${editing.id}`, body); toast.success("Güncellendi"); }
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/furniture-types/${id}`); toast.success("Silindi"); load();
  };

  const addVariation = () => setEditing({ ...editing, variations: [...(editing.variations || []), { name: "", extra_price: 0, _ck: crypto.randomUUID() }] });
  const setVariation = (i, patch) => {
    const arr = [...editing.variations];
    const cur = typeof arr[i] === "string" ? { name: arr[i], extra_price: 0, _ck: crypto.randomUUID() } : arr[i];
    // Preserve existing _ck; do NOT regenerate on edit
    arr[i] = { ...cur, ...patch };
    setEditing({ ...editing, variations: arr });
  };
  const removeVariation = (i) => setEditing({ ...editing, variations: editing.variations.filter((_, idx) => idx !== i) });

  const addExtra = () => setEditing({ ...editing, extra_fields: [...(editing.extra_fields || []), { key: "", label: "", type: "select", options: [{ name: "Yok", extra_price: 0, _ck: crypto.randomUUID() }], _ck: crypto.randomUUID() }] });
  const updExtra = (i, patch) => {
    const arr = [...editing.extra_fields];
    arr[i] = { ...arr[i], ...patch };  // preserve existing _ck
    setEditing({ ...editing, extra_fields: arr });
  };
  const removeExtra = (i) => setEditing({ ...editing, extra_fields: editing.extra_fields.filter((_, idx) => idx !== i) });

  const addExtraOption = (fi) => {
    const arr = [...editing.extra_fields];
    const opts = [...(arr[fi].options || []), { name: "", extra_price: 0, _ck: crypto.randomUUID() }];
    arr[fi] = { ...arr[fi], options: opts };
    setEditing({ ...editing, extra_fields: arr });
  };
  const updExtraOption = (fi, oi, patch) => {
    const arr = [...editing.extra_fields];
    const opts = [...(arr[fi].options || [])];
    const cur = typeof opts[oi] === "string" ? { name: opts[oi], extra_price: 0, _ck: crypto.randomUUID() } : opts[oi];
    opts[oi] = { ...cur, ...patch };
    arr[fi] = { ...arr[fi], options: opts };
    setEditing({ ...editing, extra_fields: arr });
  };
  const removeExtraOption = (fi, oi) => {
    const arr = [...editing.extra_fields];
    arr[fi] = { ...arr[fi], options: arr[fi].options.filter((_, idx) => idx !== oi) };
    setEditing({ ...editing, extra_fields: arr });
  };

  const toggle = (arr, id) => arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];

  return (
    <div data-testid="admin-furniture-types">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-black text-ink-900">Mobilya Türleri</h1>
        <Button onClick={startNew} className="gradient-primary text-white rounded-full"><Plus className="h-4 w-4 mr-1" /> Yeni Ekle</Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => setEditing({
                ...it,
                variations: (it.variations || []).map((v) => ({
                  ...(typeof v === "string" ? { name: v, extra_price: 0 } : v),
                  _ck: (typeof v === "object" && v?._ck) || crypto.randomUUID(),
                })),
                extra_fields: (it.extra_fields || []).map((f) => ({
                  ...f,
                  _ck: f?._ck || crypto.randomUUID(),
                  options: (f.options || []).map((o) =>
                    typeof o === "string"
                      ? { name: o, extra_price: 0, _ck: crypto.randomUUID() }
                      : { ...o, _ck: o?._ck || crypto.randomUUID() }
                  ),
                })),
              })}
              data-testid={`ft-card-${it.slug}`}
              className={`text-left rounded-2xl border-2 overflow-hidden bg-white transition-transform hover:-translate-y-0.5 ${editing?.id === it.id ? "border-brand-500 brand-shadow" : "border-ink-100 hover:border-brand-300"}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                {it.image_url ? <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" /> : <div className="h-full grid place-items-center bg-brand-50 text-brand-400">Görsel yok</div>}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-ink-900">{it.name}</div>
                  <span className="text-[10px] bg-brand-50 text-brand-700 rounded-full px-2 py-0.5 font-bold">{it.tier}</span>
                </div>
                <div className="text-xs text-ink-500 mt-1">{it.variations?.length || 0} alt model</div>
              </div>
            </button>
          ))}
        </div>

        <div className="rounded-3xl bg-white border border-ink-100 p-6">
          {editing ? (
            <div className="space-y-4" data-testid="ft-editor">
              <h3 className="font-display font-bold text-xl">{editing.__new ? "Yeni Tür" : "Düzenle"}</h3>
              {[
                ["name", "İsim"],
                ["slug", "Slug (URL)"],
                ["icon", "İkon (lucide)"],
              ].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs uppercase tracking-widest font-bold">{l}</Label>
                  <Input value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className="mt-1.5" />
                </div>
              ))}
              <ImageUpload
                value={editing.image_url || ""}
                onChange={(url) => setEditing({ ...editing, image_url: url, preview_url: editing.preview_url || url })}
                label="Ürün Görseli"
                testId="ft-image-url"
              />
              <ImageUpload
                value={editing.preview_url || ""}
                onChange={(url) => setEditing({ ...editing, preview_url: url })}
                label="Önizleme Görseli"
                testId="ft-preview-url"
              />
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Açıklama</Label>
                <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Görünürlük</Label>
                <Select value={editing.tier} onValueChange={(v) => setEditing({ ...editing, tier: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIERS.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Fiyat için hangi boyut sorulacak?</Label>
                <Select value={editing.dimension_mode || "wh"} onValueChange={(v) => setEditing({ ...editing, dimension_mode: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="ft-dim-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="w">Sadece Genişlik (linear)</SelectItem>
                    <SelectItem value="h">Sadece Yükseklik (linear)</SelectItem>
                    <SelectItem value="wh">Genişlik + Yükseklik (m²)</SelectItem>
                    <SelectItem value="whd">Genişlik + Yükseklik + Derinlik</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-ink-500 mt-1.5">Görünmeyen boyutlar için aşağıdaki varsayılan değerler kullanılır.</p>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Bilgi Notu (kullanıcıya gösterilir)</Label>
                <Textarea
                  rows={2}
                  value={editing.info_text || ""}
                  onChange={(e) => setEditing({ ...editing, info_text: e.target.value })}
                  className="mt-1.5"
                  placeholder="Örn: Mutfak dolabında sadece toplam genişliği girmeniz yeterli, alt/üst dolap otomatik hesaplanır."
                  data-testid="ft-info-text"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest font-bold">Vars. Genişlik (cm)</Label>
                  <Input type="number" value={editing.default_width_cm ?? 0} onChange={(e) => setEditing({ ...editing, default_width_cm: Number(e.target.value) })} className="mt-1.5" data-testid="ft-default-width" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-bold">Vars. Yükseklik (cm)</Label>
                  <Input type="number" value={editing.default_height_cm ?? 0} onChange={(e) => setEditing({ ...editing, default_height_cm: Number(e.target.value) })} className="mt-1.5" data-testid="ft-default-height" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-bold">Vars. Derinlik (cm)</Label>
                  <Input type="number" value={editing.default_depth_cm ?? 0} onChange={(e) => setEditing({ ...editing, default_depth_cm: Number(e.target.value) })} className="mt-1.5" data-testid="ft-default-depth" />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Alt Modeller + Ek Fiyatlar (Profesyonel)</Label>
                <p className="text-[11px] text-ink-500 mt-1">Fiyat modu: <b>Sabit</b> (sipariş başına) veya <b>m²</b> (her metrekare için).</p>
                <div className="space-y-2 mt-2">
                  {(editing.variations || []).map((v, i) => {
                    const cur = typeof v === "string" ? { name: v, extra_price: 0, price_mode: "flat" } : v;
                    const rowKey = cur._ck || `var-existing-${i}`;
                    return (
                      <div key={rowKey} className="flex gap-2">
                        <Input
                          placeholder="Alt model adı"
                          value={cur.name || ""}
                          onChange={(e) => setVariation(i, { name: e.target.value })}
                          className="flex-1"
                          data-testid={`variation-name-${i}`}
                        />
                        <Input
                          type="number"
                          placeholder="Ek TL"
                          value={cur.extra_price ?? 0}
                          onChange={(e) => setVariation(i, { extra_price: Number(e.target.value) })}
                          className="w-28"
                          data-testid={`variation-price-${i}`}
                        />
                        <Select
                          value={cur.price_mode || "flat"}
                          onValueChange={(m) => setVariation(i, { price_mode: m })}
                        >
                          <SelectTrigger className="w-28" data-testid={`variation-mode-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="flat">Sabit</SelectItem>
                            <SelectItem value="per_m2">m²</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={() => removeVariation(i)} className="rounded-full"><X className="h-4 w-4" /></Button>
                      </div>
                    );
                  })}
                  <Button variant="outline" onClick={addVariation} className="rounded-full text-xs"><Plus className="h-3 w-3 mr-1" /> Alt Model Ekle</Button>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">İzin Verilen Ayaklar</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {legs.map((l) => {
                    const on = (editing.allowed_leg_ids || []).includes(l.id);
                    return (
                      <button key={l.id} onClick={() => setEditing({ ...editing, allowed_leg_ids: toggle(editing.allowed_leg_ids || [], l.id) })}
                        className={`rounded-full border-2 px-3 py-1 text-xs font-semibold ${on ? "border-brand-500 bg-brand-50 text-brand-700" : "border-ink-100 text-ink-700"}`}>
                        {l.name}
                      </button>
                    );
                  })}
                  <div className="text-[11px] text-ink-500 w-full">Boş bırakırsanız hepsi kullanılır.</div>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">İzin Verilen Kapaklar</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {covers.map((c) => {
                    const on = (editing.allowed_cover_ids || []).includes(c.id);
                    return (
                      <button key={c.id} onClick={() => setEditing({ ...editing, allowed_cover_ids: toggle(editing.allowed_cover_ids || [], c.id) })}
                        className={`rounded-full border-2 px-3 py-1 text-xs font-semibold ${on ? "border-brand-500 bg-brand-50 text-brand-700" : "border-ink-100 text-ink-700"}`}>
                        {c.name}
                      </button>
                    );
                  })}
                  <div className="text-[11px] text-ink-500 w-full">Boş bırakırsanız hepsi kullanılır.</div>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Ekstra Alanlar (Profesyonel)</Label>
                <p className="text-[11px] text-ink-500 mt-1">Her ekstranın kendi seçenekleri ve ek fiyatları vardır. Varsayılan olarak &quot;Yok&quot; (0 TL) eklenir.</p>
                <div className="space-y-3 mt-2">
                  {(editing.extra_fields || []).map((f, i) => (
                    <div key={f._ck || `xf-existing-${i}`} className="rounded-xl border border-ink-100 p-3 space-y-2">
                      <div className="flex gap-2">
                        <Input placeholder="anahtar (örn: countertop)" value={f.key} onChange={(e) => updExtra(i, { key: e.target.value })} data-testid={`extra-field-key-${i}`} />
                        <Button variant="outline" onClick={() => removeExtra(i)} className="rounded-full" data-testid={`extra-field-remove-${i}`}><X className="h-4 w-4" /></Button>
                      </div>
                      <Input placeholder="etiket" value={f.label} onChange={(e) => updExtra(i, { label: e.target.value })} data-testid={`extra-field-label-${i}`} />
                      <div className="space-y-1.5 pt-1">
                        <div className="text-[11px] uppercase tracking-widest font-bold text-ink-500">Seçenekler + Ek Fiyatlar</div>
                        {(f.options || []).map((o, oi) => {
                          const cur = typeof o === "string" ? { name: o, extra_price: 0, price_mode: "flat" } : o;
                          const key = cur._ck || `xf-opt-existing-${i}-${oi}`;
                          return (
                            <div key={key} className="flex gap-2">
                              <Input
                                placeholder="Seçenek adı (örn: Mermer)"
                                value={cur.name || ""}
                                onChange={(e) => updExtraOption(i, oi, { name: e.target.value })}
                                className="flex-1"
                                data-testid={`extra-opt-name-${i}-${oi}`}
                              />
                              <Input
                                type="number"
                                placeholder="Ek TL"
                                value={cur.extra_price ?? 0}
                                onChange={(e) => updExtraOption(i, oi, { extra_price: Number(e.target.value) })}
                                className="w-24"
                                data-testid={`extra-opt-price-${i}-${oi}`}
                              />
                              <Select
                                value={cur.price_mode || "flat"}
                                onValueChange={(m) => updExtraOption(i, oi, { price_mode: m })}
                              >
                                <SelectTrigger className="w-24" data-testid={`extra-opt-mode-${i}-${oi}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="flat">Sabit</SelectItem>
                                  <SelectItem value="per_m2">m²</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button variant="outline" onClick={() => removeExtraOption(i, oi)} className="rounded-full" data-testid={`extra-opt-remove-${i}-${oi}`}><X className="h-4 w-4" /></Button>
                            </div>
                          );
                        })}
                        <Button variant="outline" onClick={() => addExtraOption(i)} className="rounded-full text-xs" data-testid={`extra-opt-add-${i}`}><Plus className="h-3 w-3 mr-1" /> Seçenek Ekle</Button>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" onClick={addExtra} className="rounded-full text-xs" data-testid="extra-field-add"><Plus className="h-3 w-3 mr-1" /> Ekstra Alan Ekle</Button>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Sıra</Label>
                <Input type="number" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} className="mt-1.5" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <span className="text-sm text-ink-500">Aktif</span>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={save} className="gradient-primary text-white rounded-full"><Save className="h-4 w-4 mr-1.5" /> Kaydet</Button>
                <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">İptal</Button>
                {!editing.__new && (
                  <Button variant="outline" className="rounded-full text-red-600 border-red-300" onClick={() => del(editing.id)}><Trash className="h-4 w-4" /></Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-ink-500 text-sm">Kart üzerine tıklayarak düzenleyebilir veya yeni ekleyebilirsiniz.</div>
          )}
        </div>
      </div>
    </div>
  );
}
